import {
  Audience,
  ConcreteExperienceMessage,
  IExperienceMessageDenormalized,
  IExperienceTemplate,
  ITaskDenormalized,
  MessageStatus
} from '@dadohr/shared/domainTypes/experience';
import { ScheduleChannel } from '@dadohr/shared/domainTypes/schedules';
import { Button, Typography } from '@material-ui/core';
import { ChevronDown, Redo, Undo } from 'lucide-react';
import moment from 'moment-timezone';
import { useMemo } from 'react';
import { ButtonWithDropdownOption } from '../../../../../components/ButtonWithDropdownOption';
import { ButtonWithoutPropagation } from '../../../../../components/ButtonWithoutPropagation';
import { ColoredChipDark } from '../../../../../components/ColoredChip';
import { useSnackbar } from '../../../../../hooks/useSnackbar';
import { useUnsafeTranslation } from '../../../../../i18next';
import { joinWithCommaAndAnd } from '../../../../../services/enumerate';
import { getItemDisplayName, getItemDisplayPair } from '../../../helpers/adminName';
import { resendExperienceItem } from '../../../../../services/experiences/instance';
import { getAllMessageTemplates } from '../../../../../services/experiences/template';
import { useCurrentUser } from '../../../../../services/currentUser';
import {
  ExperienceReplacements,
  interpolateExperienceText
} from '../../../../../services/set/interpolation';
import { ActorDescriptor } from '../../../components/Actors';
import { ActorLegend } from './ActorLegend';
import { AudienceHint } from './AudienceHint';
import { ItemTitle } from './CommonStyledComponents';
import { CustomizedTimingHint } from './CustomizedTimingHint';
import { ForceTriggerButton } from './ForceTriggerButton';
import { MessageDoneSendStatus } from './MessageDoneSendStatus';
import { MessageSendDate } from './MessageSendDate';
import { ModifiedChip } from './ModifiedChip';
import { SelectableListItem } from './SelectableListItem';

const SHOW_SKIP_MESSAGE_BUTTON = false;

// TODO pass all associated messages down
// Figure out internally what the status should be
// This also allows us to show partial successes where some parts couldn't be delivered
// Create a better data structure that makes it easier to pick these messages out as a whole
//
// Then in schedules just makes sure that whenever a hard error is thrown on send, that we truly catch all these errors and propagate them correctly.

const getMostRecentSuccessfulConcreteMessageOrAnyOther = (
  msgId: string,
  concreteMessages: ConcreteExperienceMessage[]
) => {
  const msgsForThisParticularOneInMostRecentFirstOrder = concreteMessages
    .filter((m) => m.type === 'EXPERIENCE_MESSAGE' && m.scope?.msgId === msgId)
    .reverse();
  const mostRecentlySuccessful = msgsForThisParticularOneInMostRecentFirstOrder.find(
    (m) => m.status === 'OK'
  );
  return (
    mostRecentlySuccessful ||
    msgsForThisParticularOneInMostRecentFirstOrder[0] ||
    null
  );
};

const useMostRecentSuccessfulConcreteMessageOrAnyOther = (
  msgId: string,
  concreteMessages: ConcreteExperienceMessage[]
) => {
  // Take the most recent one which was successful, or if there's none of these, the most recent in any other state
  return useMemo(
    () =>
      getMostRecentSuccessfulConcreteMessageOrAnyOther(msgId, concreteMessages),
    [msgId, concreteMessages]
  );
};

export const Message = ({
  msgId,
  selected,
  onSelect,
  actor,
  channels,
  experienceId,
  orgId,
  msg,
  onSuccessfulResend,
  onSkipMessage,
  onUnskipMessage,
  onSendToday,
  onForceTrigger,
  concreteMessages,
  audience,
  customizedTiming,
  isModified,
  isSelectable,
  triggeredBy,
  template,
  isCCView,
  replacements
}: {
  msgId: string;
  msg: IExperienceMessageDenormalized;
  actor: ActorDescriptor;
  channels: ScheduleChannel[];
  experienceId: string;
  orgId: string;
  onSkipMessage: () => void;
  onUnskipMessage: () => void;
  onSendToday: () => void;
  selected?: boolean;
  onSuccessfulResend: () => Promise<any>;
  onSelect?: (nextSelected: boolean) => void;
  onForceTrigger?: () => Promise<any>;
  concreteMessages: ConcreteExperienceMessage[];
  audience?: Audience | null;
  customizedTiming?: boolean;
  isModified?: boolean;
  isSelectable?: boolean;
  triggeredBy?: ITaskDenormalized[];
  template?: IExperienceTemplate;
  isCCView?: boolean;
  replacements?: ExperienceReplacements;
}) => {
  const { t } = useUnsafeTranslation();
  const { org } = useCurrentUser();
  const { enqueueSnackbarSuccess, enqueueSnackbarError } = useSnackbar();

  const handleChannelSelectToResend = async ({
    orgId,
    experienceId,
    itemId,
    actor,
    channels
  }: {
    orgId: string;
    experienceId: string;
    itemId: string;
    actor: string;
    channels: ScheduleChannel[];
  }) => {
    await resendExperienceItem({
      orgId,
      experienceId,
      itemId,
      itemType: 'MESSAGE',
      actor,
      channels,
      includedTaskStartIds: []
    })
      .then(() => {
        if (msg.status === MessageStatus.ERROR) {
          onSuccessfulResend();
        }
        enqueueSnackbarSuccess('Message resent successfully!');
      })
      .catch(() => enqueueSnackbarError('Failed to resend message!'));
  };

  const renderMessageStatusChip = (status: MessageStatus) => {
    switch (status) {
      case MessageStatus.SKIPPED:
        return (
          <ColoredChipDark size="small" color="darkGray">
            {t('Skipped')}
          </ColoredChipDark>
        );
      case MessageStatus.UPCOMING:
        return (
          <ColoredChipDark size="small" color="gray">
            {t('Upcoming')}
          </ColoredChipDark>
        );
      default:
        return <MessageDoneSendStatus concreteMessages={concreteMessages} />;
    }
  };

  const isInFuture = (): boolean => {
    if (msg.sendAt) {
      const automationDate = moment.unix(msg.sendAt.seconds);
      const days = automationDate.diff(new Date(), 'days');
      if (days > 0) {
        return true;
      }
    }
    return false;
  };

  const isUntriggered = !!triggeredBy?.length;

  const msgTpl = template
    ? getAllMessageTemplates(template.messages).find((tpl) => tpl.id === msgId)
    : undefined;
  const { primary: rawPrimary, secondary: rawSecondary } = getItemDisplayPair(msgTpl ?? msg);
  // See Task.tsx for the rationale: prefer parent-built replacements so
  // role tokens like {{ participant.fullName }} actually resolve. The
  // empty fallback preserves prior behavior for callers that don't yet
  // thread replacements through.
  const interpolationReplacements: ExperienceReplacements = replacements ?? {
    experience: { vars: { title: template?.title ?? '', tasks: {} } },
    org: { vars: { name: org.name } },
    roles: []
  };
  const interpolationOpts = { dateFormat: 'YYYY/MM/DD' };
  const displayPrimary = interpolateExperienceText(rawPrimary, interpolationReplacements, interpolationOpts);
  const displaySecondary = rawSecondary
    ? interpolateExperienceText(rawSecondary, interpolationReplacements, interpolationOpts)
    : undefined;

  return (
    <SelectableListItem
      isSelectable={
        /* looks weird but is needed because of default */
        isUntriggered ? false : isSelectable
      }
      selected={selected}
      onSelect={onSelect}
      containerStyle={isCCView ? { opacity: 0.5 } : undefined}
      header={
        <>
          {renderMessageStatusChip(msg.status)}
          <ActorLegend type="message" color={actor.color} isCCView={isCCView}>
            {actor.label}
          </ActorLegend>
          <ItemTitle>
            <strong>{displayPrimary}</strong>
            {displaySecondary && (
              <span style={{ fontStyle: 'italic' }}>
                {' '}"{displaySecondary}"
              </span>
            )}
            <AudienceHint type="message" audience={audience} />
            <CustomizedTimingHint
              type="message"
              customizedTiming={customizedTiming}
              at={msg.sendAt}
            />
            <ModifiedChip active={isModified} />

            {SHOW_SKIP_MESSAGE_BUTTON && (
              <Button onClick={onSkipMessage}>{t('Skip message')}</Button>
            )}
          </ItemTitle>
        </>
      }
      headerActions={
        <>
          {!isUntriggered && (
            <>
              {msg.status !== MessageStatus.UPCOMING &&
                msg.status !== MessageStatus.SKIPPED && (
                  <ButtonWithDropdownOption
                    size="small"
                    variant="outlined"
                    pending={t('Sending...')}
                    options={channels}
                    allOptionLabel={t('All channels')}
                    endIcon={<ChevronDown size={16} />}
                    onSelect={(selectedChannel: ScheduleChannel[]) =>
                      handleChannelSelectToResend({
                        orgId,
                        experienceId,
                        itemId: msg.templateId,
                        actor: actor.key,
                        channels: selectedChannel
                      })
                    }
                  >
                    {t('Resend')}
                  </ButtonWithDropdownOption>
                )}
              {msg.status === MessageStatus.UPCOMING && (
                <ButtonWithoutPropagation
                  startIcon={<Redo size={16} />}
                  variant="outlined"
                  type="button"
                  size="small"
                  onClick={() => onSkipMessage()}
                >
                  {t('Skip')}
                </ButtonWithoutPropagation>
              )}
              {isInFuture() && msg.status === MessageStatus.UPCOMING && (
                <ButtonWithoutPropagation
                  variant="outlined"
                  type="button"
                  size="small"
                  onClick={() => onSendToday()}
                >
                  {t('Send today')}
                </ButtonWithoutPropagation>
              )}
              {msg.status === MessageStatus.SKIPPED && (
                <ButtonWithoutPropagation
                  startIcon={<Undo size={16} />}
                  variant="outlined"
                  type="button"
                  size="small"
                  onClick={() => onUnskipMessage()}
                >
                  {t('Unskip')}
                </ButtonWithoutPropagation>
              )}
            </>
          )}
          
          {template && 
            <ForceTriggerButton
              item={msg}
              itemType={'message'}
              triggeredBy={triggeredBy || []}
              isUntriggered={isUntriggered}
              template={template}
              onForceTrigger={onForceTrigger}
              enqueueSnackbarSuccess={enqueueSnackbarSuccess}
              enqueueSnackbarError={enqueueSnackbarError}
              t={t}
            />
          }
        </>
      }
      subHeader={
        triggeredBy?.length ? (
          <Typography variant="caption" component="div">
            {t('After completion of')}:{' '}
            <strong>
              {joinWithCommaAndAnd(
                triggeredBy.map((x) => getItemDisplayName(x)),
                t
              )}
            </strong>
          </Typography>
        ) : (
          <Typography variant="caption" component="div">
            <MessageSendDate msg={msg} concreteMessages={concreteMessages} />
          </Typography>
        )
      }
    />
  );
};
